import { Injectable, NgZone } from '@angular/core';
import { Observable, filter, map, take, timeout, timer } from 'rxjs';
import { UserService } from './user.service';

export interface LocationSelection {
  address: string;
  latitude: number;
  longitude: number;
  addressComponent?: google.maps.GeocoderAddressComponent[];
  name?: string;
}
export interface LocationSuggestion { description: string; place_id: string; }

export function validLocation(value: LocationSelection | null | undefined): value is LocationSelection {
  return !!value && Number.isFinite(value.latitude) && Number.isFinite(value.longitude)
    && Math.abs(value.latitude) <= 90 && Math.abs(value.longitude) <= 180;
}

export function locationCity(location: LocationSelection): string | null {
  for (const type of ['locality', 'postal_town', 'administrative_area_level_3', 'administrative_area_level_2']) {
    const component = location?.addressComponent?.find(item => item.types.includes(type));
    if (component?.long_name) return component.long_name;
  }
  return null;
}

export function locationError(error: any): string {
  return error instanceof LocationLookupError ? error.message : 'Unable to load locations. Please try again.';
}
class LocationLookupError extends Error {}

@Injectable({ providedIn: 'root' })
export class LocationLookupService {
  constructor(private userService: UserService, private zone: NgZone) {}

  search(query: string): Observable<LocationSuggestion[]> {
    return this.userService.getPlace('api/mapdetails/getsearchresults', encodeURIComponent(query.trim())).pipe(
      timeout(15000), map(response => {
        this.checkStatus(response?.status);
        if (!Array.isArray(response?.predictions)) throw new LocationLookupError('Unable to load locations. Please try again.');
        return response.predictions.filter(item => item?.place_id && item?.description);
      })
    );
  }

  coordinates(suggestion: LocationSuggestion): Observable<LocationSelection> {
    return this.userService.getPlace('api/mapdetails/getcoordinates', encodeURIComponent(suggestion.place_id)).pipe(
      timeout(15000), map(response => {
        this.checkStatus(response?.status);
        return this.toSelection(response?.results?.[0], suggestion.description);
      })
    );
  }

  waitForMaps(): Observable<void> {
    // index.html owns the SDK script. Wait for it instead of loading another copy.
    return timer(0, 100).pipe(
      filter(() => typeof google !== 'undefined' && !!google.maps?.Map && !!google.maps?.Geocoder
        && !!google.maps?.marker?.AdvancedMarkerElement),
      take(1), timeout(10000), map(() => undefined)
    );
  }

  reverse(position: google.maps.LatLngLiteral): Observable<LocationSelection> {
    return new Observable<LocationSelection>(subscriber => {
      new google.maps.Geocoder().geocode({ location: position }, (results, status) => {
        if (subscriber.closed) return;
        this.zone.run(() => { try {
          this.checkStatus(status);
          subscriber.next(this.toSelection(results?.[0]));
          subscriber.complete();
        } catch (error) { subscriber.error(error); } });
      });
    }).pipe(timeout(15000));
  }

  private checkStatus(status: string): void {
    if (status === 'OK' || status === 'ZERO_RESULTS') return;
    if (status === 'OVER_QUERY_LIMIT' || status === 'OVER_DAILY_LIMIT' || status === 'REQUEST_DENIED') {
      throw new LocationLookupError('Location search is temporarily unavailable. Please try again later.');
    }
    throw new LocationLookupError('Unable to load locations. Please try again.');
  }

  private toSelection(result: any, address?: string): LocationSelection {
    const point = result?.geometry?.location;
    const selection: LocationSelection = {
      address: address || result?.formatted_address || '',
      latitude: typeof point?.lat === 'function' ? point.lat() : point?.lat,
      longitude: typeof point?.lng === 'function' ? point.lng() : point?.lng,
      addressComponent: result?.address_components || [],
      name: ''
    };
    if (!validLocation(selection) || !selection.address) {
      throw new LocationLookupError('No address was found for this location. Please choose another location.');
    }
    return selection;
  }
}
